import { useApolloClient } from '@apollo/client'
import {
  AttendanceEmployeeFiltersInput,
  BulkCreateAttendanceReviewsDocument,
  BulkDestroyAttendanceReviewsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  cycle: string
  startOn: string
  endOn: string
}

interface BulkActionParams {
  employeeIds: string[]
  selectAll?: boolean
  excludedEmployeeIds?: string[]
  filters?: AttendanceEmployeeFiltersInput | null
}

export const useBulkApprovePeriod = ({ cycle, startOn, endOn }: Props) => {
  const client = useApolloClient()
  const [bulkCreateAttendanceReviews] = useMutation(BulkCreateAttendanceReviewsDocument, {
    extractorFn: (data) => data.attendanceMutations.bulkCreateAttendanceReview,
  })

  const [bulkDestroyAttendanceReviews] = useMutation(BulkDestroyAttendanceReviewsDocument, {
    extractorFn: (data) => data.attendanceMutations.bulkDestroyAttendanceReview,
  })

  const bulkApprovePeriod = async ({
    employeeIds,
    selectAll,
    excludedEmployeeIds,
    filters,
  }: BulkActionParams) => {
    const payload: {
      employeeIds: string[]
      startOn: string
      endOn: string
      selectAll?: boolean
      excludedEmployeeIds?: string[]
      filters?: AttendanceEmployeeFiltersInput | null
    } = {
      employeeIds,
      startOn,
      endOn,
    }

    if (selectAll) {
      payload.employeeIds = []
      payload.excludedEmployeeIds = excludedEmployeeIds
      payload.filters = filters || null
    }

    const result = await bulkCreateAttendanceReviews({ variables: payload })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      const trackingData = {
        employee_ids: employeeIds,
        employee_count: employeeIds.length,
        period_type: cycle,
        start_on: startOn,
        end_on: endOn,
      }

      track(trackingEvents.timeTracking.bulkApprovePeriodFromTimesheet, trackingData)

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      const refetchResult = await client.refetchQueries({
        include: [
          'AttendanceCustomPeriodEmployeesCollectionList',
          'AttendanceCustomPeriodEmployeesAnalytics',
        ],
      })
      return refetchResult
    }
    return null
  }

  const bulkDisapprovePeriod = async ({
    employeeIds,
    selectAll,
    excludedEmployeeIds,
    filters,
  }: BulkActionParams) => {
    const payload: {
      employeeIds: string[]
      startOn: string
      endOn: string
      selectAll?: boolean
      excludedEmployeeIds?: string[]
      filters?: AttendanceEmployeeFiltersInput | null
    } = {
      employeeIds,
      startOn,
      endOn,
    }

    if (selectAll) {
      payload.employeeIds = []
      payload.excludedEmployeeIds = excludedEmployeeIds
      payload.filters = filters || null
    }

    const result = await bulkDestroyAttendanceReviews({ variables: payload })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      const trackingData = {
        employee_ids: employeeIds,
        employee_count: employeeIds.length,
        period_type: cycle,
        start_on: startOn,
        end_on: endOn,
      }

      track(trackingEvents.timeTracking.bulkDisapprovePeriodFromTimesheet, trackingData)

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      const refetchResult = await client.refetchQueries({
        include: [
          'AttendanceCustomPeriodEmployeesCollectionList',
          'AttendanceCustomPeriodEmployeesAnalytics',
        ],
      })
      return refetchResult
    }
    return null
  }

  return {
    bulkApprovePeriod,
    bulkDisapprovePeriod,
  }
}
